import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldAlert, X } from 'lucide-react';
import { useEstadoCertificados } from '@/hooks/useEstadoCertificados';
import {
  describirAviso,
  ocultarSieteDias,
  ocultoHasta,
  type Almacen,
  type ColorAviso,
} from '@/utils/avisoCertificado';
import { cn } from '@/lib/utils';

const CLASES: Record<ColorAviso, string> = {
  amarillo: 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-700 dark:bg-yellow-950 dark:text-yellow-100',
  naranja: 'border-orange-300 bg-orange-50 text-orange-900 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-100',
  rojo: 'border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-100',
};

/** `localStorage` puede lanzar al acceder a él (modo privado, datos bloqueados). */
function almacenLocal(): Almacen | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Aviso global de caducidad del certificado digital (debajo de la cabecera,
 * visible en toda la aplicación). El color nunca es lo único que informa: lleva
 * icono y texto, y los niveles urgentes se anuncian con `role="alert"`.
 */
export function AvisoCertificado() {
  const { estado } = useEstadoCertificados();
  // Id del certificado cuyo aviso se ocultó en esta sesión: el de otro certificado sí se muestra.
  const [ocultadoId, setOcultadoId] = useState<string | null>(null);

  const aviso = describirAviso(estado);
  if (!aviso) return null;

  const activoId = estado?.activo?.id;
  if (aviso.ocultable && activoId && (ocultadoId === activoId || ocultoHasta(almacenLocal(), new Date(), activoId))) {
    return null;
  }

  const Icono = aviso.color === 'rojo' ? ShieldAlert : AlertTriangle;

  return (
    <div
      role={aviso.rol}
      className={cn('flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm', CLASES[aviso.color])}
    >
      <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="flex-1">{aviso.texto}</p>
      <Link to={aviso.accion.a} className="font-semibold underline underline-offset-2">
        {aviso.accion.texto}
      </Link>
      {aviso.ocultable && activoId && (
        <button
          type="button"
          aria-label="Ocultar este aviso durante 7 días"
          className="rounded p-1 hover:bg-black/10"
          onClick={() => {
            ocultarSieteDias(almacenLocal(), new Date(), activoId);
            setOcultadoId(activoId);
          }}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
